/**
 * ExecutionFeed Component
 *
 * Real-time execution stream with status badges and progress indicators.
 * Shows latest test executions with live updates.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  MapPin,
  Smartphone,
  ExternalLink,
  Ban,
  AlertTriangle,
} from 'lucide-react';
import { Card, Badge, Spinner } from '@/components/ui';
import { useRealtimeExecutions, RealtimeExecution, ExecutionStatus } from '@/hooks/useRealtimeExecutions';
import { formatDistanceToNow } from 'date-fns';

interface ExecutionFeedProps {
  limit?: number;
  showTitle?: boolean;
  onExecutionClick?: (executionId: string) => void;
}

const STATUS_CONFIG: Record<ExecutionStatus, {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
  bgColor: string;
  animate?: boolean;
}> = {
  pending: {
    icon: Clock,
    label: 'Pending',
    color: 'text-status-pending',
    bgColor: 'bg-status-pending-bg',
  },
  running: {
    icon: Loader2,
    label: 'Running',
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue/10',
    animate: true,
  },
  completed: {
    icon: CheckCircle2,
    label: 'Completed',
    color: 'text-status-success',
    bgColor: 'bg-status-success-bg',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    color: 'text-status-error',
    bgColor: 'bg-status-error-bg',
  },
  cancelled: {
    icon: Ban,
    label: 'Cancelled',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
  },
};

/**
 * Format duration in human readable format
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '--';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

/**
 * Check if execution is running longer than expected (5 minutes)
 */
function isLongRunning(execution: RealtimeExecution): boolean {
  if (execution.status !== 'running' && execution.status !== 'pending') return false;
  if (!execution.startedAt) return false;
  const elapsedMs = Date.now() - execution.startedAt.getTime();
  return elapsedMs > 5 * 60 * 1000; // 5 minutes
}

/**
 * Get elapsed time for running executions
 */
function getElapsedSeconds(startedAt: Date | null): number | null {
  if (!startedAt) return null;
  return Math.floor((Date.now() - startedAt.getTime()) / 1000);
}

/**
 * Single execution item in the feed
 */
const ExecutionItem: React.FC<{
  execution: RealtimeExecution;
  onClick?: () => void;
}> = ({ execution, onClick }) => {
  const config = STATUS_CONFIG[execution.status] || STATUS_CONFIG.pending;
  const Icon = config?.icon || Clock;
  const longRunning = isLongRunning(execution);
  const isRunning = execution.status === 'running' || execution.status === 'pending';

  // For running executions, show elapsed time instead of duration
  const displayDuration = isRunning
    ? getElapsedSeconds(execution.startedAt)
    : execution.duration;

  return (
    <div
      onClick={onClick}
      className={`
        flex items-start gap-3 p-3 rounded-lg border
        hover:border-border-default hover:bg-muted/50
        transition-all cursor-pointer group
        ${execution.status === 'running' ? 'border-accent-blue/50 ring-1 ring-accent-blue/30' : 'border-border'}
        ${longRunning ? 'border-status-warning/50 ring-1 ring-status-warning/30' : ''}
      `}
    >
      {/* Status Icon */}
      <div className={`mt-0.5 ${longRunning ? 'text-status-warning' : config.color}`}>
        {longRunning ? (
          <AlertTriangle className="w-5 h-5" />
        ) : (
          <Icon className={`w-5 h-5 ${config.animate ? 'animate-spin' : ''}`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-foreground truncate">
            {execution.flowName}
          </span>
          <Badge
            className={`${longRunning ? 'bg-status-warning-bg text-status-warning' : `${config.bgColor} ${config.color}`} text-xs px-1.5 py-0`}
          >
            {longRunning ? 'Long Running' : config.label}
          </Badge>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {execution.locationName && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {execution.locationName}
            </span>
          )}
          {execution.modemName && (
            <span className="flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              {execution.modemName}
            </span>
          )}
        </div>

        {/* Long running warning message */}
        {longRunning && (
          <p className="text-xs text-status-warning mt-1">
            Running longer than expected ({formatDuration(displayDuration)})
          </p>
        )}

        {/* Error message for failed executions */}
        {execution.status === 'failed' && execution.error && (
          <p className="text-xs text-status-error mt-1 truncate">
            {execution.error}
          </p>
        )}
      </div>

      {/* Right side: Time and duration */}
      <div className="text-right flex-shrink-0">
        <div className={`text-xs font-mono ${longRunning ? 'text-status-warning' : 'text-muted-foreground'}`}>
          {formatDuration(displayDuration)}
        </div>
        {execution.startedAt && (
          <div className="text-xs text-muted-foreground/70 mt-1">
            {formatDistanceToNow(execution.startedAt, { addSuffix: true })}
          </div>
        )}
      </div>

      {/* Hover indicator */}
      <ExternalLink className="w-4 h-4 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

/**
 * ExecutionFeed Component
 */
export const ExecutionFeed: React.FC<ExecutionFeedProps> = ({
  limit = 10,
  showTitle = true,
  onExecutionClick,
}) => {
  const navigate = useNavigate();
  const { executions, runningCount, loading, error } = useRealtimeExecutions({
    limitCount: limit,
    includeCompleted: true,
  });

  const handleExecutionClick = (executionId: string) => {
    if (onExecutionClick) {
      onExecutionClick(executionId);
    } else {
      navigate(`/executions/${executionId}`);
    }
  };

  return (
    <Card padding="lg">
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-h3 font-semibold text-foreground">
              Real-Time Executions
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Live test execution stream
            </p>
          </div>
          {runningCount > 0 && (
            <Badge className="bg-accent-blue/10 text-accent-blue animate-pulse">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              {runningCount} running
            </Badge>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-muted-foreground">Loading executions...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex items-center justify-center py-8 text-status-error">
          <XCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && executions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <PlayCircle className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">No recent executions</p>
          <p className="text-xs mt-1">Run a test to see it here</p>
        </div>
      )}

      {/* Execution list */}
      {!loading && !error && executions.length > 0 && (
        <div className="space-y-2">
          {executions.map((execution) => (
            <ExecutionItem
              key={execution.id}
              execution={execution}
              onClick={() => handleExecutionClick(execution.id)}
            />
          ))}
        </div>
      )}

      {/* View all link */}
      {executions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={() => navigate('/executions')}
            className="text-sm text-accent-blue hover:text-accent-blue/80 transition-colors flex items-center gap-1"
          >
            View all executions
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </Card>
  );
};

export default ExecutionFeed;
